import { html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import { nsLayoutStyles } from '../../../ns-styles/index.js';
import NSJobComponent from '../job.js';
import JobSchema from '../../../models/job-schema.js';
import type { TypeUi } from '../../../models/company-data.js';
import { readJobData, type JobData } from '../../../helpers/company-data.js';
import { dataFields, type DataField } from '../../../views/data/definition.js';
import { formatCell } from '../../../views/data/records-table.js';
import { jobFormValues, pageLabel } from '../../../views/data/job-values.js';
import { renderFormField } from '../../../helpers/form-field.js';
import { nsFormFieldStyles } from '../../../ns-styles/index.js';
import '../../../ns-elements/ns-loading.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/data-select-record.json';
import { jobSchema } from '../job-schema.js';

// Pick one record from a data set, seeing only the fields the workflow maps, through the job's own access
@customElement(NSDataSelectRecord.is)
export default class NSDataSelectRecord extends NSJobComponent {
  static get is() {
    return 'ns-data-select-record';
  }

  @state()
  private __data?: JobData;

  @state()
  private __fields: DataField[] = [];

  @state()
  private __picked = '';

  @state()
  private __search = '';

  @state()
  private __page = 1;

  private __searchTimer?: number;

  @state()
  private __error = '';

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { selected: { type: 'hidden' } } });
  }

  static styles = [
    nsLayoutStyles,
    nsFormFieldStyles,
    ...super.styles,
    css`
      .content {
        display: flex;
        flex-direction: column;
        gap: var(--wa-space-m);
        height: 100%;
      }

      fieldset {
        border: 0;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: var(--wa-space-xs);
      }

      label {
        display: flex;
        gap: var(--wa-space-s);
        align-items: baseline;
      }

      .values {
        color: var(--wa-color-text-quiet);
      }

      .pager {
        display: flex;
        align-items: center;
        gap: var(--wa-space-s);
      }

      .error {
        color: var(--wa-color-danger-on-quiet);
      }
    `,
  ];

  async connectedCallback() {
    await super.connectedCallback();
    await this.__read();
    // The job's access lets the policy say a record changed; what changed is read again through the lambda
    this._subscriptions.subscribe('dataRecord.*', (cr: { path?: string }) =>
      this.__onRecordChange(cr.path?.split('.')[1]),
    );
  }

  private __rereadTimer?: number;

  // Other sets' records change all the time; only this job's set is worth a read
  private async __onRecordChange(recordId: string | undefined) {
    if (!recordId || !this.__data || !this._db) return;
    const known = this.__data.records.some((record) => record.id === recordId);
    const changed = known ? null : await this._db.getById<{ id: string; dataSetId?: string }>('dataRecord', recordId);
    if (!known && changed?.dataSetId !== this.__data.set.id) return;
    window.clearTimeout(this.__rereadTimer);
    this.__rereadTimer = window.setTimeout(() => this.__read(), 300);
  }

  private async __read() {
    try {
      this.__data = await readJobData(this._auth?.token, this.job.id, { search: this.__search, page: this.__page });
      this.__fields = dataFields(this.__data.definition, {
        ...this.__data.ui,
        titleField: this.__data.ui.titleField ?? '',
      } as TypeUi);
    } catch (err) {
      this.__error = err instanceof Error ? err.message : 'Unable to read the records for this job.';
    }
  }

  private async __select() {
    if (!this.__picked) {
      this.__error = 'Pick a record first.';
      return;
    }
    try {
      this.loading('Using that record');
      await this.outcome('selected', { RECORD: this.__picked });
      this.loaded();
    } catch (err) {
      this.loaded();
      this.failJob(err, 'Unable to use that record');
    }
  }

  private __summary(raw: Record<string, unknown>) {
    const { values, titles, hidden } = jobFormValues(this.__fields, raw);
    return this.__fields
      .filter((field) => !field.isTitle && (values[field.key] !== undefined || hidden.includes(field.key)))
      .map(
        (field) =>
          `${field.label}: ${hidden.includes(field.key) ? 'Not shown in this job' : formatCell(field, values[field.key], titles)}`,
      )
      .join(' · ');
  }

  private __onSearch(text: string) {
    this.__search = text;
    window.clearTimeout(this.__searchTimer);
    this.__searchTimer = window.setTimeout(() => {
      this.__page = 1;
      this.__read();
    }, 300);
  }

  private __turn(page: number) {
    this.__page = page;
    this.__read();
  }

  private __renderPager(data: JobData) {
    const pages = Math.max(1, Math.ceil(data.total / data.pageSize));
    if (pages === 1) return nothing;
    return html`
      <div class="pager">
        <wa-button size="small" ?disabled=${data.page <= 1} @click=${() => this.__turn(data.page - 1)}>
          Previous
        </wa-button>
        <span role="status">${pageLabel(data.page, data.pageSize, data.total)}</span>
        <wa-button size="small" ?disabled=${data.page >= pages} @click=${() => this.__turn(data.page + 1)}>
          Next
        </wa-button>
      </div>
    `;
  }

  renderActions() {
    if (!this.__data) return nothing;
    return html`
      <wa-button variant="brand" type="button" @click=${() => this.__select()}>Use this record</wa-button>
    `;
  }

  render() {
    const prompt = this.job.inputValues?.PROMPT;
    if (!this.__data) {
      return this.__error
        ? html`
            <p class="error" role="alert">${this.__error}</p>
          `
        : html`
            <ns-loading label="Loading records..."></ns-loading>
          `;
    }
    return html`
      <div class="content">
        ${renderFormField({
          id: 'record-search',
          label: 'Search records',
          control: html`
            <input
              id="record-search"
              type="search"
              .value=${this.__search}
              @input=${(e: Event) => this.__onSearch((e.target as HTMLInputElement).value)}
            />
          `,
        })}
        <fieldset>
          <legend>
            ${typeof prompt === 'string' && prompt ? prompt : `Pick a record from ${this.__data.set.name}`}
          </legend>
          ${this.__data.records.length === 0
            ? html`
                <p>
                  ${this.__search ? 'No records match your search.' : `${this.__data.set.name} has no records yet.`}
                </p>
              `
            : this.__data.records.map(
                (record, index) => html`
                  <label>
                    <input
                      type="radio"
                      name="record"
                      .value=${record.id}
                      ?checked=${this.__picked === record.id}
                      @change=${() => (this.__picked = record.id)}
                    />
                    <span>
                      <strong>${record.title || `Record ${index + 1}`}</strong>
                      <span class="values">${this.__summary(record.values)}</span>
                    </span>
                  </label>
                `,
              )}
        </fieldset>
        ${this.__renderPager(this.__data)}
        ${this.__error
          ? html`
              <p class="error" role="alert">${this.__error}</p>
            `
          : nothing}
      </div>
    `;
  }
}
